/**
 * useBlockReorder — HTML5 drag-and-drop hook for same-type, same-parent reorder.
 *
 * Phase 1: drop indicator rendered by the caller between siblings; on drop,
 * computes new `sortOrder` midpoint between neighbors and submits via reorder API.
 * Cross-parent moves are deferred (Phase 3).
 */
import { useCallback, useRef, useState } from 'react'

export interface BlockMeta {
  type: 'epic' | 'story' | 'nfr-category'
  id: string
  parentId: string | null
  /** Current sortOrder at the start of the drag (for midpoint math). */
  sortOrder: number
}

interface Options {
  /** Called with the new ordered list of sibling ids (full reindex). */
  onCommit: (parentId: string | null, orderedIds: string[]) => void | Promise<void>
  /** Return all siblings (same type, same parent) in current sort order. */
  getSiblings: (meta: BlockMeta) => BlockMeta[]
}

interface DragState {
  dragging: BlockMeta | null
  /** Hover target: the block we'd drop relative to. `position` says above/below. */
  overTarget: BlockMeta | null
  position: 'above' | 'below' | null
}

export function useBlockReorder({ onCommit, getSiblings }: Options) {
  const [state, setState] = useState<DragState>({ dragging: null, overTarget: null, position: null })
  const ref = useRef(state)
  ref.current = state

  const makeDraggableProps = useCallback((meta: BlockMeta) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setState({ dragging: meta, overTarget: null, position: null })
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', meta.id)
        // Use the full block wrapper as the drag ghost (not the tiny handle).
        // Each block wrapper is marked with data-block-id=<id>.
        const handleEl = e.currentTarget as HTMLElement
        const blockEl = handleEl.closest<HTMLElement>(`[data-block-id="${meta.id}"]`)
        if (blockEl) {
          const r = blockEl.getBoundingClientRect()
          const offsetX = e.clientX - r.left
          const offsetY = e.clientY - r.top
          try { e.dataTransfer.setDragImage(blockEl, offsetX, offsetY) } catch { /* no-op on older browsers */ }
        }
      }
    },
    onDragEnd: () => {
      setState({ dragging: null, overTarget: null, position: null })
    },
  }), [])

  const makeDropZoneProps = useCallback((meta: BlockMeta) => ({
    'data-block-id': meta.id,
    onDragOver: (e: React.DragEvent) => {
      const d = ref.current.dragging
      if (!d) return
      if (d.type !== meta.type) return                  // same type only
      if ((d.parentId ?? null) !== (meta.parentId ?? null)) return // same parent only
      if (d.id === meta.id) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const above = (e.clientY - rect.top) < rect.height / 2
      setState(prev => ({ ...prev, overTarget: meta, position: above ? 'above' : 'below' }))
    },
    onDragLeave: () => {
      setState(prev => {
        if (prev.overTarget?.id !== meta.id) return prev
        return { ...prev, overTarget: null, position: null }
      })
    },
    onDrop: async (e: React.DragEvent) => {
      const d = ref.current.dragging
      if (!d) return
      if (d.type !== meta.type) return
      if ((d.parentId ?? null) !== (meta.parentId ?? null)) return
      if (d.id === meta.id) return
      e.preventDefault()

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const above = (e.clientY - rect.top) < rect.height / 2

      // Compute the new sibling order after the drop.
      const siblings = getSiblings(meta).filter(s => s.id !== d.id)
      const targetIdx = siblings.findIndex(s => s.id === meta.id)
      if (targetIdx < 0) {
        setState({ dragging: null, overTarget: null, position: null })
        return
      }
      const insertAt = above ? targetIdx : targetIdx + 1
      const next = [...siblings]
      next.splice(insertAt, 0, d)
      const orderedIds = next.map(s => s.id)

      setState({ dragging: null, overTarget: null, position: null })
      await onCommit(d.parentId, orderedIds)
    },
  }), [getSiblings, onCommit])

  const isDragTargetAbove = (meta: BlockMeta) =>
    state.overTarget?.id === meta.id && state.position === 'above'
  const isDragTargetBelow = (meta: BlockMeta) =>
    state.overTarget?.id === meta.id && state.position === 'below'
  const isDragging = (meta: BlockMeta) => state.dragging?.id === meta.id

  /**
   * Keyboard-reorder handler for accessibility parity with drag.
   * Bind to the drag handle (as focusable element). On Ctrl/⌘+Shift+ArrowUp/Down
   * (also plain Alt+ArrowUp/Down), move the block within its sibling list.
   */
  const makeKeyboardReorderProps = useCallback((meta: BlockMeta): React.HTMLAttributes<HTMLElement> & { tabIndex: number } => ({
    tabIndex: 0,
    role: 'button',
    'aria-label': `Drag ${meta.type} to reorder. Alt+ArrowUp / Alt+ArrowDown to reorder with keyboard.`,
    onKeyDown: async (e: React.KeyboardEvent) => {
      const isUp = e.key === 'ArrowUp'
      const isDown = e.key === 'ArrowDown'
      if (!isUp && !isDown) return
      // Modifier gate: Alt (most forgiving, no conflict with text nav), OR Ctrl/Cmd+Shift.
      const mod = e.altKey || ((e.ctrlKey || e.metaKey) && e.shiftKey)
      if (!mod) return
      e.preventDefault()
      e.stopPropagation()

      const siblings = getSiblings(meta)
      const currentIdx = siblings.findIndex(s => s.id === meta.id)
      if (currentIdx < 0) return
      const targetIdx = isUp ? currentIdx - 1 : currentIdx + 1
      if (targetIdx < 0 || targetIdx >= siblings.length) return

      const next = [...siblings]
      next.splice(currentIdx, 1)
      next.splice(targetIdx, 0, meta)
      await onCommit(meta.parentId, next.map(s => s.id))
    },
  }), [getSiblings, onCommit])

  return {
    makeDraggableProps, makeDropZoneProps, makeKeyboardReorderProps,
    isDragging, isDragTargetAbove, isDragTargetBelow,
  }
}

